<template>
  <div class="expense-details">
    <div class="header">
      <div>Expense Report</div>
      <div>Reference# {{ expense.referenceNumber }}</div>
    </div>
    <div class="logo-status-header">
      <div class="tes-logo"></div>
      <div><Status :status="expense.status" object-type="expense" /></div>
    </div>
    <div class="expense-header">
      <div class="header-item">
        <div class="header-label">Pay Period</div>
        <div class="header-value">
          <span class="nowrap">{{ startDate | formatDate }}</span>
          to
          <span class="nowrap">{{ endDate | formatDate }}</span>
        </div>
      </div>
      <div class="header-item">
        <div class="header-label">Contractor</div>
        <div class="header-value bolded">
          {{ expense.contractor.lastName + ', ' + expense.contractor.lastName }}(#{{
            expense.assignment.payrollNums[0].payroll_num
          }})
        </div>
      </div>
      <div class="header-item">
        <div class="header-label">Client</div>
        <div class="header-value bolded">{{ expense.assignment.name }}</div>
      </div>
    </div>
    <div>
      <table class="expense-details-body-table">
        <thead>
          <tr>
            <th>Date (yyyy-mm-dd)</th>
            <th>Description</th>
            <th>Units?</th>
            <th>Unit #</th>
            <th></th>
            <th>Unit Price</th>
            <th></th>
            <th>Notes</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(entry, index) in entries"
            :key="entry.id"
            :class="index % 2 == 0 ? 'odd' : 'even'"
          >
            <td class="date">{{ entry.entryDate | formatDateIgnoreTime('YYYY-MM-DD ddd') }}</td>
            <td>{{ entry.entryNote }}</td>
            <td>
              {{ entry.isUnit }}
            </td>
            <td>
              {{ entry.unitQuantity }}
            </td>
            <td>x</td>
            <td>
              {{ entry.unitPrice }}
            </td>
            <td>
              =
            </td>
            <td>
              {{ entry.amount }}
            </td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <th>Expenses Totals</th>
            <th>{{ this.expenseNote }}</th>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            <th></th>
            <th>
              {{ grandTotal }}
            </th>
          </tr>
        </tfoot>
      </table>
    </div>
    <div class="expense-attachments">
      <div class="expense-attachments-header">
        Invoice/ Documents Attached
      </div>
      <div>
        <ul>
          <li v-for="(attachment, index) in attachments" :key="index">
            {{ attachment.category }} {{ attachment.originalName }} was updated by
            {{ attachment.last_name }}, {{ attachment.first_name }} on
            {{ attachment.createdAt | formatDateTime }}
          </li>
        </ul>
      </div>
    </div>
    <div class="expense-history">
      <div class="expense-history-header">Document History</div>
      <div>
        <table class="expense-history-table">
          <thead>
            <tr>
              <th>Date (yyyy-mm-dd)</th>
              <th>Status</th>
              <th>User</th>
              <th>Comments</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(historyRecord, index) in expenseHistories"
              :key="index"
              :class="index % 2 == 0 ? 'even' : 'odd'"
            >
              <td class="date">{{ historyRecord.createdAt | formatDateTime }}</td>
              <td>{{ historyRecord.status | readable | capitalize }}</td>
              <td>{{ historyRecord.createdBy | nameFromUserObject }}</td>
              <td>{{ historyRecord.approverComment }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import * as R from 'ramda';
import Status from '@/app/components/Status';

export const sumExpenses = entries => {
  let totalExpenses = 0;
  R.map(entry => {
    totalExpenses += parseFloat(entry.amount);
  })(entries);
  return totalExpenses.toFixed(2);
};

export default {
  name: 'ExpenseDetailsPdf',
  components: { Status },
  props: {
    expense: {
      type: Object,
      required: true
    },
    expenseAttachments: {
      type: Array,
      default: null
    },
    history: {
      type: Array,
      default: null
    },
    batch: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      entries: [],
      expenseNote: ''
    };
  },
  computed: {
    expenseHistories() {
      if (this.batch) {
        return this.history.filter(historyRecord => historyRecord.id !== this.expense.id);
      }
      return this.$store.state.expensesState.expenseDetailsState.expenseHistories.filter(
        historyRecord => historyRecord.id !== this.expense.id
      );
    },
    grandTotal() {
      return sumExpenses(this.entries);
    },
    // startDate and endDate computed functions are only required right now because
    // the api does not return these values. Once the api has been changed to return
    // all the fields from the time_sheets_view, we won't need these two functions.
    startDate() {
      return this.expense.endDate;
    },
    endDate() {
      return this.expense.startDate;
    },
    attachments() {
      if (this.batch) {
        this.expenseAttachments;
      }
      return this.expense.attachments
        ? this.expense.attachments
        : this.$store.state.expensesState.expenseDetailsState.attachments;
    }
  },
  watch: {
    expense: {
      immediate: true,
      handler() {
        this.expenseNote = this.expense.expenseNote;
        if (this.expense.expenseEntries !== undefined) {
          this.entries = R.clone(this.expense.expenseEntries);
        }
      }
    }
  }
};
</script>
<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';
.expense-details {
  margin: 1% 5%;
  font-size: 1rem;
  .header {
    display: flex;
    flex-direction: row;
    color: $grey;
    padding-top: 0px;
    padding-bottom: 5px;
    justify-content: space-between;
    font-size: 0.6rem;
  }
  .logo-status-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
  .expense-header {
    display: flex;
    flex-direction: column;
    break-inside: avoid;
    width: 100%;
    .header-left-content {
      width: 50%;
    }
    .header-item {
      display: flex;
      flex-direction: row;
      break-inside: avoid;
      .header-label {
        font-weight: bold;
      }
      .header-value {
        padding-left: 10%;
      }
    }
  }
  .expense-details-body-table,
  .expense-history-table {
    font-size: 0.9rem;
    margin-top: 3%;
    thead {
      border-bottom: 0.5pt solid $black;
      font-weight: bold;
      page-break-inside: avoid;
      padding-bottom: 1px;
    }
    tbody {
      tr {
        border-bottom: 0.25pt solid $row-border;
        padding-bottom: 0%;
        margin-bottom: 0%;
      }
    }
    .even {
      background-color: $background-level-3;
    }
  }
  .bolded {
    font-weight: bold;
  }
}
.expense-history,
.expense-attachments {
  margin-top: 3%;
  .expense-history-header,
  .expense-attachments-header {
    font-size: 1rem;
    font-weight: bold;
  }
}
</style>
